import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Search, ArrowRight, Check, X } from 'lucide-react'
import { Button, TextField, Checkbox, Spinner } from '@anyemp/ui-kit'
import { talents as talentService } from '../../services/apiService'
import ModalPortal from '../UI/ModalPortal'
import ModalFrame from '../UI/ModalFrame'

const MOVED_LABELS = {
  contacts: 'Contacts',
  communications: 'Communications',
  notes: 'Notes',
  important_dates: 'Important dates',
  links: 'Links',
  professions: 'Professions',
  tools: 'Tools',
  responsibilities: 'Responsibilities',
  languages: 'Languages',
  media: 'Media',
  cv: 'CV entries',
  talent_posts: 'Posts',
  playbook_instances: 'Playbooks',
  salary: 'Salary',
  ai_settings: 'AI settings',
  public_settings: 'Public settings',
  job_application: 'Job application'
}

const talentLabel = (t) => {
  if (!t) return ''
  const name = [t.first_name, t.last_name].filter(Boolean).join(' ') || t.name || `#${t.id}`
  return `${name}`
}

const talentMeta = (t) => {
  if (!t) return ''
  const status = t.currentStatus?.name || t.status?.name
  return [t.talent_id || `#${t.id}`, t.talent_type, status, t.is_active === false ? 'inactive' : null]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Объединение дубликата с талантом.
 *
 * Применить объединение можно только после успешного предпросмотра ровно этой пары:
 * при смене дубликата протокол сбрасывается, и кнопка снова блокируется.
 */
const MergeTalentModal = ({ talent, onClose, onMerged }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [source, setSource] = useState(null)

  const [preview, setPreview] = useState(null)
  const [previewFor, setPreviewFor] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)

  const [force, setForce] = useState(false)
  const [error, setError] = useState('')
  const [needsForce, setNeedsForce] = useState(false)
  const [merging, setMerging] = useState(false)

  const searchTimer = useRef(null)

  // Поиск кандидатов на объединение
  useEffect(() => {
    if (source) return
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      return
    }

    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await talentService.getTalents({ search: term, limit: 8 })
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response?.data?.data || []
        setResults(list.filter((t) => t.id !== talent.id))
      } catch (err) {
        console.error('Error searching talents:', err)
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 350)

    return () => clearTimeout(searchTimer.current)
  }, [query, source, talent.id])

  // Смена дубликата обесценивает предыдущий протокол
  const selectSource = (t) => {
    setSource(t)
    setResults([])
    setQuery('')
    setPreview(null)
    setPreviewFor(null)
    setError('')
    setNeedsForce(false)
    setForce(false)
  }

  const resetSource = () => {
    setSource(null)
    setPreview(null)
    setPreviewFor(null)
    setError('')
    setNeedsForce(false)
    setForce(false)
  }

  const runPreview = async () => {
    if (!source) return
    setLoadingPreview(true)
    setError('')
    try {
      const response = await talentService.mergeTalent(talent.id, source.id, { dryRun: true, force })
      setPreview(response.data)
      setPreviewFor(source.id)
      setNeedsForce(false)
    } catch (err) {
      const status = err.response?.status
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      setError(message)
      setNeedsForce(status === 409)
      setPreview(null)
      setPreviewFor(null)
    } finally {
      setLoadingPreview(false)
    }
  }

  const applyMerge = async () => {
    if (!canApply) return
    setMerging(true)
    setError('')
    try {
      const response = await talentService.mergeTalent(talent.id, source.id, {
        dryRun: false,
        force,
        confirmToken: preview.confirm_token
      })
      onMerged?.(response.data, source)
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message)
      setMerging(false)
    }
  }

  // Применяем только то, что только что посмотрели, и ровно ту же пару
  const canApply = Boolean(preview && source && previewFor === source.id && !merging)

  const movedRows = useMemo(() => {
    if (!preview) return []
    return Object.entries(preview.moved || {})
      .filter(([, value]) => value)
      .map(([key, value]) => [MOVED_LABELS[key] || key, value])
  }, [preview])

  const skippedRows = useMemo(() => {
    if (!preview) return []
    return Object.entries(preview.skipped || {})
      .filter(([, value]) => value)
      .map(([key, value]) => [MOVED_LABELS[key] || key, value])
  }, [preview])

  return (
    <ModalPortal onClose={merging ? undefined : onClose} ariaLabel="Merge talent">
      <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
        <ModalFrame.Header className="items-center">
          <h3 className="text-base font-semibold text-fg-primary">Merge duplicate</h3>
          <button type="button" onClick={onClose} disabled={merging} aria-label="Close" className="inline-flex h-10 w-10 items-center justify-center rounded-md text-fg-tertiary hover:bg-bg-secondary disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>
        <ModalFrame.Body className="space-y-4">

        <div className="rounded-md border border-divider p-3">
          <div className="text-xs font-medium text-fg-secondary mb-1">Keep this profile</div>
          <div className="text-sm font-medium text-fg-primary">{talentLabel(talent)}</div>
          <div className="text-xs text-fg-tertiary">{talentMeta(talent)}</div>
        </div>

        {!source ? (
          <div>
            <TextField
              label="Find the duplicate"
              value={query}
              onChange={(e) => setQuery(e.target?.value ?? e)}
              placeholder="Name, email or phone"
              startAdornment={<Search className="h-4 w-4" />}
              fullWidth
            />
            {searching && <div className="mt-2 text-xs text-fg-tertiary">Searching…</div>}
            {!searching && results.length > 0 && (
              <div className="mt-2 rounded-md border border-divider">
                {results.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => selectSource(t)}
                    className="w-full text-left px-3 py-2 border-b last:border-0 border-divider hover:bg-bg-secondary"
                  >
                    <div className="text-sm text-fg-primary">{talentLabel(t)}</div>
                    <div className="text-xs text-fg-tertiary">{talentMeta(t)}</div>
                  </button>
                ))}
              </div>
            )}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <div className="mt-2 text-xs text-fg-tertiary">Nothing found</div>
            )}
          </div>
        ) : (
          <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">
                  This profile will be removed
                </div>
                <div className="text-sm font-medium text-fg-primary">{talentLabel(source)}</div>
                <div className="text-xs text-fg-tertiary">{talentMeta(source)}</div>
              </div>
              <Button variant="text" color="neutral" size="small" onClick={resetSource}>
                Change
              </Button>
            </div>
          </div>
        )}

        {source && !preview && (
          <Button
            color="primary"
            variant="outlined"
            onClick={runPreview}
            disabled={loadingPreview}
            fullWidth
          >
            {loadingPreview ? 'Checking…' : 'Preview changes'}
          </Button>
        )}

        {error && (
          <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
            <div className="flex gap-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
            {needsForce && (
              <div className="mt-2">
                <Checkbox
                  checked={force}
                  onChange={(e) => setForce(e.target?.checked ?? e)}
                  label="These are the same person — merge anyway"
                />
                <Button
                  className="mt-2"
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={runPreview}
                  disabled={!force || loadingPreview}
                >
                  Preview again
                </Button>
              </div>
            )}
          </div>
        )}

        {loadingPreview && (
          <div className="flex justify-center py-2"><Spinner size="small" /></div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-fg-secondary">
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              Preview only — nothing has been changed yet
            </div>

            <div className="border border-divider rounded-md divide-y divide-divider text-xs">
              <div className="px-3 py-2 font-medium text-fg-secondary flex items-center gap-1">
                Moves to {talentLabel(talent)} <ArrowRight className="h-3 w-3" />
              </div>
              {movedRows.length ? movedRows.map(([label, value]) => (
                <div key={label} className="flex flex-wrap justify-between gap-2 px-3 py-1.5">
                  <span className="min-w-0 text-fg-primary">{label}</span>
                  <span className="text-fg-tertiary">{value}</span>
                </div>
              )) : (
                <div className="px-3 py-1.5 text-fg-tertiary">Nothing to move</div>
              )}
            </div>

            {skippedRows.length > 0 && (
              <div className="border border-divider rounded-md divide-y divide-divider text-xs">
                <div className="px-3 py-2 font-medium text-fg-secondary">Dropped as duplicates</div>
                {skippedRows.map(([label, value]) => (
                  <div key={label} className="flex flex-wrap justify-between gap-2 px-3 py-1.5">
                    <span className="text-fg-primary">{label}</span>
                    <span className="text-fg-tertiary">{value}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-red-300 dark:border-red-800 rounded-md divide-y divide-red-200 dark:divide-red-900 text-xs">
              <div className="px-3 py-2 font-medium text-red-700 dark:text-red-300">Will be deleted</div>
              {preview.deleted?.events > 0 && (
                <div className="px-3 py-1.5 flex justify-between">
                  <span className="text-fg-primary">Events of the duplicate</span>
                  <span className="text-fg-tertiary">{preview.deleted.events}</span>
                </div>
              )}
              {preview.deleted?.job_application && (
                <div className="px-3 py-1.5 flex justify-between">
                  <span className="text-fg-primary">Job application</span>
                  <span className="text-fg-tertiary">#{preview.deleted.job_application}</span>
                </div>
              )}
              <div className="px-3 py-1.5 flex justify-between">
                <span className="text-fg-primary">Talent profile</span>
                <span className="text-fg-tertiary">#{preview.source?.id} {preview.source?.talent_id}</span>
              </div>
            </div>

            {preview.filled_fields?.length > 0 && (
              <div className="text-xs text-fg-secondary">
                Empty fields to be filled: {preview.filled_fields.join(', ')}
              </div>
            )}

            {preview.warnings?.length > 0 && (
              <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1">
                {preview.warnings.map((warning, i) => (
                  <div key={i} className="flex gap-2 text-xs text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span className="break-all">{warning}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
          <Button variant="outlined" color="neutral" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          <Button color="error" onClick={applyMerge} disabled={!canApply}>
            {merging ? 'Merging…' : 'Merge and delete duplicate'}
          </Button>
        </ModalFrame.Footer>
      </ModalFrame>
    </ModalPortal>
  )
}

export default MergeTalentModal
